<template>
  <section class="app-community-wapper">
    <section class="app-community-menu-page">
      <!-- TITLE -->
      <CommonDocumentHeader :border="false">
        <div class="flex lg:items-center max-lg:items-start">
          <GfrNewButton class="app-toggle-btn flex-shrink-0 lg:!hidden" @click="toggleCommunityMenu">
            <IconsReturn class="h-4 w-4" />
          </GfrNewButton>
          <GfrTitle
            :level="1"
            :placement="$route.params.lang === 'ar' ? 'right' : 'left'"
            class="app-docs-menu-page-title"
          >
            {{ route.params.category as string }}
          </GfrTitle>
        </div>
      </CommonDocumentHeader>
      <!-- SEARCH -->
      <div
        class="app-community-search"
        :class="[currentActiveMenuType === 'video' ? 'h-8' : 'h-0', { 'filter-trigger': filterTrigger }]"
      >
        <template v-if="currentActiveMenuType === 'video'">
          <CommonDocumentInputFilter
            v-model="filterValue"
            v-model:trigger="filterTrigger"
            class="app-community-search-filter"
            :placeholder="formatTransify('UGC_OFFICIAL_WEBSITE_FILTER_TEXT')"
          />
          <div v-show="filterTrigger" class="app-community-search-filter-tip" v-html="formatFilterTip" />
          <!-- SELECT -->
          <!-- -->

          <div
            class="app-community-search-filter-tag"
            :class="{ open: filterTriggerTag }"
            role="button"
            tabindex="0"
            aria-label="Open filter dropdown"
            v-clickout="filterTagClickClose"
            :aria-expanded="filterTriggerTag ? 'true' : 'false'"
            @click="handleFilterTagClick"
            @keydown.enter.prevent="handleFilterTagClick"
            @keydown.space.prevent="handleFilterTagClick"
          >
            <div class="app-community-search-filter-tag-inner">
              <div
                class="app-community-search-filter-tag-corner group"
                :data-state="filterTriggerTag ? 'open' : 'close'"
              >
                <IconsArrow
                  class="portrait:max-lg:hidden transition-transform duration-300 group-data-[state=open]:rotate-180"
                />
                <IconsSwap
                  class="text-[#d1d1d1] lg:hidden landscape:hidden portrait:max-lg:visible group-data-[state=open]:text-primary max-lg:h-[14px] max-lg:w-[14px]"
                />
              </div>
              <div class="app-community-search-filter-tag-label">
                <span>{{ activeFilterTag?.text ?? '' }}</span>
              </div>
              <!-- 下拉选项容器：以 `.app-community-search-filter-tag` 作为父容器 -->

              <div v-show="filterTriggerTag" class="app-community-search-filter-dropdown" role="listbox" @click.stop>
                <button
                  v-for="tag in filterTagList"
                  :key="tag.id"
                  class="app-community-search-filter-dropdown-item"
                  type="button"
                  role="option"
                  :aria-selected="tag.id === activeFilterTagId ? 'true' : 'false'"
                  @click.stop="handleSelectFilterTag(tag.id)"
                  @keydown.enter.prevent.stop="handleSelectFilterTag(tag.id)"
                >
                  <span class="inline-block overflow-hidden text-ellipsis whitespace-nowrap w-full">{{
                    tag.text
                  }}</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="app-community-module">
        <div class="app-community-module-list">
          <CommonCard
            v-for="(item, idx) in currentCommunityData"
            :key="idx"
            :title="item.title"
            :image="item.image"
            :url="item.url"
            :video="currentActiveMenuType === 'video'"
            :isLiked="item.isLike"
            :video-tag="formatTransify(item.tag || '')"
            :likeCount="formateLikeNum(item.like)"
            :description="currentActiveMenuType === 'video' ? formatTime(item.publish_time) : ''"
            @licke-icon-click-emit="debounceVideoLikeHandler(item)"
          />
        </div>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
useRouteCache((route) => {
  route.setCacheable().setMaxAge(300).addTags(['community-page-cache-tag'])
})
const route = useRoute()
const store = useStore()
const communityStore = useCommunityStore()
const { formatTransify } = store
const {
  toggleCommunityMenu,
  filterCommunityVideo,
  searchByTag,
  searchByName,
  formateLikeNum,
  videoLikeHandler,
  updateVideoLike,
  isVideoCategory
} = communityStore
const { communityPageDataMap } = storeToRefs(communityStore)
const filterValue = ref('')
const filterTrigger = ref(false)
// 筛选下拉触发层
const filterTriggerTag = ref(false)
const filterTagList = ref([
  {
    id: 1,
    text: formatTransify('UGC_OFFICIAL_WEBSITE_WIKI_FILTER1'),
    name: 'Recently Updated -mockname'
  },
  {
    id: 2,
    text: formatTransify('UGC_OFFICIAL_WEBSITE_WIKI_FILTER2'),
    name: 'Most Liked -mockname'
  }
])
const activeFilterTagId = ref<number>(filterTagList.value[0]?.id ?? 0)
const activeFilterTag = computed(() => {
  return filterTagList.value.find((item) => item.id === activeFilterTagId.value)
})
function handleSelectFilterTag(id: number) {
  activeFilterTagId.value = id
  filterTriggerTag.value = false
}
// 点击“筛选下拉触发层”：打开下拉选项（同时支持键盘 Enter/Space）
const handleFilterTagClick = () => {
  filterTriggerTag.value ? filterTagClickClose() : (filterTriggerTag.value = true)
}
const filterTagClickClose = () => {
  filterTriggerTag.value = false
}
const debounceVideoLikeHandler = debounce(videoLikeHandler, 200, true)

const formatFilterTip = computed(() => {
  return formatTransify('UGC_OFFICIAL_WEBSITE_SEARCHED').replace(
    '{num}',
    `<span class="text-white">${currentCommunityData.value.length}</span>`
  )
})
const currentCommunityData = computed(() => {
  let result = communityPageDataMap.value.get(route.params.category as string) || []
  if (currentActiveMenuType.value === 'video') {
    if (filterTrigger.value) {
      result = filterCommunityVideo(result, searchByName(filterValue.value))
    }
    if (activeFilterTag.value && activeFilterTag.value.id === 2) {
      result = [...result].sort((a, b) => b.like - a.like)
    }
  }
  return result
})
const currentActiveMenuType = computed(() => {
  return isVideoCategory(route.params.category as string) ? 'video' : 'module'
})
onMounted(async () => {
  if (currentActiveMenuType.value === 'video') {
    await updateVideoLike()
  }
})
</script>

<style scoped lang="scss">
.app-docs-menu-page-title {
  @apply text-[30px]/[38px] font-medium uppercase;
  @apply max-lg:text-[22px]/[32px];
}
.app-toggle-btn {
  @apply inline-flex z-10 h-8 w-8 bg-dark-background ltr:mr-2 rtl:ml-2;
}
.app-community-search {
  @apply z-10;
  @apply relative mt-8 mb-6 w-full max-lg:mt-4 max-lg:mb-4;
  @apply flex items-center;
}
.app-community-search.filter-trigger {
  @apply max-2xl:mb-[34px];
}
.app-community-search-filter-tip {
  @apply max-2xl:absolute bottom-[-26px];
  @apply text-m-p text-light;
}
.app-community-search-filter {
  @apply lg:w-[312px] landscape:max-lg:w-[416px]  max-lg:w-[295px];
  @apply ltr:lg:mr-2 rtl:lg:ml-2;
}
.app-community-search-filter-tag {
  // 边框/背景色对齐 InputFilter：border-[#999]/25 + bg-dark-background
  @apply border border-solid border-[#999]/25 bg-dark-background;
  // 相对 `.app-community-search`（已是 relative）定位：右对齐，高度跟随父容器 100%
  @apply absolute top-0 ltr:right-0 h-full;
  @apply rtl:left-0;
  // 宽度：PC(>=lg) 224px；移动端横屏 203px；移动端竖屏 32px
  @apply lg:w-[224px] landscape:max-lg:w-[203px] max-lg:portrait:w-8;
  @apply transition-colors duration-300;
}
.app-community-search-filter-tag.open {
  @apply lg:border-primary landscape:max-lg:border-primary;
}
.app-community-search-filter-tag-inner {
  @apply relative h-[30px] w-full top-0 left-0;
  // padding 对齐 InputFilter（考虑 LTR/RTL）
  @apply ltr:pl-3 ltr:pr-8 rtl:pl-8 rtl:pr-3 py-0.5;
  // 作为 dropdown 的定位父容器
  @apply portrait:max-lg:p-0;
  // 需求：PC + 移动端横屏下 padding-right 固定 32px（覆盖 RTL 的 pr-3）
  @apply ltr:landscape:max-lg:pr-8 rtl:landscape:max-lg:pl-8;
  // Figma Mobile：控件高度 32px，文字 12px/18px
  @apply max-lg:h-full max-lg:flex max-lg:items-center;
}
.app-community-search-filter-tag-corner {
  @apply flex items-center justify-center;
  // 32x32 的绝对定位块：PC/横屏右上，竖屏左上
  @apply absolute top-0 h-8 w-8  pointer-events-none;
  @apply max-lg:portrait:left-0 max-lg:portrait:right-auto;
  @apply ltr:right-0 rtl:left-0;
}
.app-community-search-filter-tag-label {
  // 让“已选项”在 tag 区域内可见；竖屏 32px 宽时默认不展示文本，避免溢出
  // 文字样式对齐 InputFilter：text-sm + leading-6 + text-white
  @apply h-full w-full px-0 text-[14px]/[22px] leading-6 text-white;
  @apply max-lg:portrait:hidden;
  @apply lg:flex lg:items-center;
  @apply max-lg:landscape:flex max-lg:landscape:items-center;

  // PC + 移动端横屏：单行展示，溢出隐藏（省略号）
  @apply lg:truncate landscape:max-lg:truncate;
  // Figma Mobile：12px / 18px
  @apply max-lg:text-xs max-lg:leading-[18px];
}
.app-community-search-filter-dropdown {
  // 下拉选项：以 `.app-community-search-filter-tag` 为父容器
  // - 宽度：与父容器宽度一致
  // - 高度：32px
  @apply absolute ltr:left-0 rtl:right-0 top-[32px] z-20;
  // 视觉上先给个最基础的容器样式，方便调试（可按设计再细化）
  @apply overflow-hidden border border-solid border-[#999]/25 bg-[#141414];
  //
  @apply lg:w-full;
  @apply max-lg:w-[203px] ltr:max-lg:right-0 ltr:max-lg:left-auto;
  @apply rtl:max-lg:left-0 rtl:max-lg:right-auto;
}
.app-community-search-filter-dropdown-item {
  @apply block h-8 w-full px-2 text-left text-[14px]/[32px] text-[#d1d1d1] bg-[#141414];
  @apply hover:text-primary;
  @apply transition-colors duration-300;
  // Figma Mobile 下拉项：12px / 18px
  @apply max-lg:text-xs max-lg:leading-[18px];

  &[aria-selected='true'] {
    @apply bg-[#363A41] text-primary;
  }
}
.app-community-module {
  @apply mb-8 last:mb-0 max-lg:mb-4 max-lg:last:mb-0;
}
.app-community-module-header {
  @apply mb-6 max-lg:mb-2;
}
.app-community-module-list {
  @apply grid grid-cols-3 gap-6 max-lg:portrait:grid-cols-1 lg:max-2xl:grid-cols-2;
  @apply max-lg:portrait:gap-2 max-lg:landscape:gap-x-2 max-lg:landscape:gap-y-4;
}
</style>
